import type { RefObject } from 'react';
import { ActionIcon, Badge, Box, Center, Group, Paper, Stack, Text, Tooltip, UnstyledButton, useMantineColorScheme } from '@mantine/core';
import { IconLogout, IconMoon, IconSettings, IconSun } from '@tabler/icons-react';
import type { AuthSessionUser } from '@domain/auth/types';
import type { ContactDirectoryUser } from '@domain/contacts/types';
import { UserAvatar } from '@shared/ui/UserAvatar';
import { BrandLogo } from '@shared/ui/BrandLogo';
import { initials } from '@shared/utils/ui';
import { getWorkspaceNavigationItems, type ActiveTab } from './workspaceNavigationItems';

function DesktopUserMenuItem({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      px="sm"
      py={8}
      role="menuitem"
      style={{
        display: 'flex',
        width: '100%',
        borderRadius: 10,
        transition: 'background-color 160ms ease',
      }}
      styles={{
        root: {
          '&:hover': {
            backgroundColor: danger ? 'rgba(179, 58, 58, 0.08)' : 'rgba(196, 90, 60, 0.08)',
          },
        },
      }}
    >
      <Group gap="sm" wrap="nowrap" align="center" w="100%">
        <Center c={danger ? 'red.6' : 'terracotta.7'} w={22}>{icon}</Center>
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={650} c={danger ? 'red.6' : undefined}>
            {label}
          </Text>
        </Stack>
      </Group>
    </UnstyledButton>
  );
}

type WorkspaceNavRailProps = {
  currentUser: AuthSessionUser;
  cdnBase: string;
  ownerContact?: ContactDirectoryUser;
  ownerAvatarUrl?: string;
  profileSignature: string;
  isOnline: boolean;
  connState: 'idle' | 'connecting' | 'online' | 'error';
  activeTab: ActiveTab;
  totalUnreadCount: number;
  pendingRequestCount: number;
  isUserMenuOpen: boolean;
  userMenuRef: RefObject<HTMLDivElement | null>;
  userMenuBtnRef: RefObject<HTMLButtonElement | null>;
  onNavigate: (value: ActiveTab) => void;
  setIsUserMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleLogout: () => void;
};

export function WorkspaceNavRail({
  currentUser,
  cdnBase,
  ownerContact,
  ownerAvatarUrl,
  profileSignature,
  isOnline,
  connState,
  activeTab,
  totalUnreadCount,
  pendingRequestCount,
  isUserMenuOpen,
  userMenuRef,
  userMenuBtnRef,
  onNavigate,
  setIsUserMenuOpen,
  handleLogout,
}: WorkspaceNavRailProps) {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const userTitle = currentUser.displayName || '用户';
  const userSubtitle = profileSignature.trim() || ownerContact?.signInfo?.trim() || '';
  const navigationItems = getWorkspaceNavigationItems({
    totalUnreadCount,
    pendingRequestCount,
    iconSize: 26,
  });

  return (
    <Stack
      component="nav"
      data-testid="workspace-nav-rail"
      w="var(--workspace-rail-width)"
      h="100%"
      style={{
        position: 'relative',
        borderRight: '1px solid rgba(216, 186, 103, 0.22)',
        background: [
          'linear-gradient(180deg, var(--locs-signature-dark) 0%, var(--locs-espresso) 100%)',
          'repeating-linear-gradient(108deg, rgba(255,248,231,0.055) 0 1px, transparent 1px 12px)',
        ].join(', '),
      }}
      align="center"
      justify="space-between"
      py="md"
    >
      <Stack gap="md" align="center">
        {navigationItems
          // 设置入口统一放在底部用户头像菜单中，主导航栏不再重复展示。
          .filter((item) => item.value !== 'settings')
          .map((item) => {
          const active = activeTab === item.value;
          return (
            <Box key={item.value} pos="relative">
              <Tooltip label={item.label} position="right" withArrow openDelay={250}>
                <ActionIcon
                  onClick={() => onNavigate(item.value)}
                  size={48}
                  radius="md"
                  variant="subtle"
                  aria-label={item.ariaLabel}
                  aria-current={active ? 'page' : undefined}
                  title={item.label}
                  style={{
                    color: active ? 'var(--locs-espresso)' : 'rgba(255, 248, 231, 0.68)',
                    backgroundColor: active ? 'var(--locs-metal-line)' : 'transparent',
                  }}
                >
                  {item.icon}
                </ActionIcon>
              </Tooltip>
              {item.badge !== undefined && item.badge > 0 && (
                <Badge
                  size="xs"
                  circle
                  color="terracotta"
                  variant="filled"
                  style={{ position: 'absolute', top: 2, right: 2, pointerEvents: 'none' }}
                >
                  {item.badge > 99 ? '99+' : item.badge}
                </Badge>
              )}
            </Box>
          );
          })}
      </Stack>

      <Stack gap="md" align="center">
        <Box pos="relative">
          <UnstyledButton
            ref={userMenuBtnRef}
            onClick={() => setIsUserMenuOpen((open) => !open)}
            style={{ borderRadius: '50%' }}
          aria-label="打开账户菜单"
          aria-haspopup="menu"
          aria-expanded={isUserMenuOpen}
        >
          <UserAvatar
            id={currentUser.id}
            name={currentUser.displayName || 'User'}
            size={48}
            fallback={initials(currentUser.displayName, String(currentUser.id).slice(-2))}
            avatarKind="human"
            avatarUrl={ownerAvatarUrl || ownerContact?.avatarUrl}
            cdnBase={cdnBase}
          />
          <Box
            pos="absolute"
            bottom={2}
            right={2}
            w={10}
            h={10}
            style={{
              borderRadius: '50%',
              border: '2px solid var(--locs-espresso)',
              backgroundColor: isOnline ? 'var(--locs-forest)' : 'var(--locs-text-muted)',
              zIndex: 1,
            }}
          />
        </UnstyledButton>
      </Box>
      </Stack>

      <Paper
        shadow="md"
        p={6}
        radius="lg"
        withBorder
        role="menu"
        aria-label="账户菜单"
        style={{
          zIndex: 100,
          bottom: 16,
          left: 'calc(var(--workspace-rail-width) - 8px)',
          width: 220,
          opacity: isUserMenuOpen ? 1 : 0,
          visibility: isUserMenuOpen ? 'visible' : 'hidden',
          transition: 'opacity 0.18s ease, transform 0.18s ease',
          transform: isUserMenuOpen ? 'translateX(0)' : 'translateX(-6px)',
          background: 'var(--locs-panel)',
          borderColor: 'var(--locs-border)',
        }}
        ref={userMenuRef}
        pos="absolute"
      >
        <Group gap="sm" wrap="nowrap" p="sm">
          <UserAvatar
            id={currentUser.id}
            name={currentUser.displayName || 'User'}
            size={44}
            fallback={initials(currentUser.displayName, String(currentUser.id).slice(-2))}
            avatarKind="human"
            avatarUrl={ownerAvatarUrl || ownerContact?.avatarUrl}
            cdnBase={cdnBase}
          />
        <Stack gap={1} style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={850} truncate>{userTitle}</Text>
            {userSubtitle && <Text size="xs" c="dimmed" truncate>{userSubtitle}</Text>}
            <Group gap={6} align="center" mt={2}>
              <Box
                w={8}
                h={8}
                style={{
                  borderRadius: '50%',
                  backgroundColor: isOnline
                    ? 'var(--locs-forest)'
                    : connState === 'connecting'
                    ? 'var(--locs-gold)'
                    : 'var(--locs-text-muted)',
                }}
              />
              <Text size="xs" c="dimmed" fw={600}>
                {isOnline ? '在线' : connState === 'connecting' ? '连接中' : '离线'}
              </Text>
            </Group>
          </Stack>
        </Group>
        <Stack gap={1}>
          <DesktopUserMenuItem
            icon={colorScheme === 'dark' ? <IconSun size={17} stroke={2} /> : <IconMoon size={17} stroke={2} />}
            label={colorScheme === 'dark' ? '浅色模式' : '深色模式'}
            onClick={() => {
              setColorScheme(colorScheme === 'dark' ? 'light' : 'dark');
            }}
          />
          <DesktopUserMenuItem
            icon={<IconSettings size={17} stroke={2} />}
            label="设置"
            onClick={() => {
              setIsUserMenuOpen(false);
              onNavigate('settings');
            }}
          />
        </Stack>
        <DesktopUserMenuItem
          icon={<IconLogout size={17} stroke={2} />}
          label="退出登录"
          danger
          onClick={() => {
            setIsUserMenuOpen(false);
            handleLogout();
          }}
        />
      </Paper>
    </Stack>
  );
}
